import type {
  WhatsappEditTemplate,
  WhatsappOriginalTemplateButtons,
  WhatsappOriginalTemplateComponent,
  WhatsappTemplateButtonsType,
} from 'types/templates';
import { generateUniqueId } from 'utils/helpers';
import { varEndRegular } from 'utils/regexes';
import type { SmartTemplateAiPayload } from './smartTemplateAiTypes';
import { normalizeWhatsappBodyPlaceholders } from './AIGeneratorServices';

function collectBodyVarIndices(body: string): number[] {
  const set = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    set.add(parseInt(m[1], 10));
  }
  return Array.from(set).sort((a, b) => a - b);
}

/** One sample per placeholder occurrence in order (matches Meta editor body_text[0]). */
function buildBodyExampleRow(
  bodyText: string,
  examples: Record<string, string> | undefined,
): string[] | undefined {
  const matches = [...bodyText.matchAll(/\{\{(\d+)\}\}/g)];
  if (matches.length === 0) return undefined;

  const defaultSamples = [
    'דני',
    'חברת דוגמה בע״מ',
    'ישראל ישראלי',
    'הזמנה #1001',
    'תל אביב',
    'לקוח יקר',
    'מוצר לדוגמה',
    'שירות לקוחות',
    'sales@example.com',
    '050-0000000',
  ];

  return matches.map((m, i) => {
    const n = m[1];
    const fromAi = examples?.[n] ?? examples?.[String(parseInt(n, 10))];
    const trimmed = (fromAi ?? '').trim();
    if (trimmed) return trimmed;
    return defaultSamples[i % defaultSamples.length];
  });
}

/** Editor shows URL sample only when URL ends with {{n}} (see createEdit index URL button UI). */
function urlButtonNeedsExample(url: string | null | undefined): boolean {
  const u = normalizeWhatsappBodyPlaceholders((url ?? '').trim());
  return varEndRegular.test(u);
}

/** Quick replies first, then URL — matches WhatsApp ordering rules in this editor. */
function sortButtonsForWhatsApp(
  buttons: SmartTemplateAiPayload['buttons'],
): SmartTemplateAiPayload['buttons'] {
  const qr = buttons.filter((b) => b.type === 'QUICK_REPLY');
  const url = buttons.filter((b) => b.type === 'URL');
  return [...qr, ...url];
}

export function applySmartTemplatePayloadToTemplate(
  prev: WhatsappEditTemplate,
  payload: SmartTemplateAiPayload,
): WhatsappEditTemplate {
  const sortedButtons = sortButtonsForWhatsApp(payload.buttons);
  const varIndices = collectBodyVarIndices(payload.body_text);
  const exampleRow = buildBodyExampleRow(payload.body_text, payload.body_parameter_examples);

  const bodyComponent: WhatsappOriginalTemplateComponent = {
    type: 'BODY',
    format: null,
    text: payload.body_text,
    buttons: null,
    example:
      varIndices.length > 0 && exampleRow && exampleRow.length > 0
        ? {
            body_text: [exampleRow],
          }
        : undefined,
  };

  const existingIds: string[] = [];
  const mappedButtons = sortedButtons.map((btn) => {
    const id = generateUniqueId(existingIds);
    existingIds.push(id);
    if (btn.type === 'QUICK_REPLY') {
      return {
        id,
        type: 'QUICK_REPLY' as WhatsappTemplateButtonsType,
        text: btn.text,
      };
    }
    const urlNorm = normalizeWhatsappBodyPlaceholders((btn.url ?? '').trim()) || null;
    const baseUrl: {
      id: string;
      type: WhatsappTemplateButtonsType;
      text: string;
      url: string | null;
      example?: string[];
    } = {
      id,
      type: 'URL' as WhatsappTemplateButtonsType,
      text: btn.text,
      url: urlNorm,
    };
    if (urlButtonNeedsExample(urlNorm)) {
      const sample = (btn.url_variable_example ?? '').trim() || 'https://example.com/offer/12345';
      baseUrl.example = [sample];
    }
    return baseUrl;
  });

  const buttonsComponent = {
    type: 'BUTTONS' as const,
    format: null,
    text: null,
    buttons: mappedButtons,
  } as WhatsappOriginalTemplateButtons;

  const rest = prev.components.filter((c) => c.type !== 'BODY' && c.type !== 'BUTTONS');
  const headers = rest.filter((c) => c.type === 'HEADER');
  const footers = rest.filter((c) => c.type === 'FOOTER');

  const category = payload.category ?? 'MARKETING';

  return {
    ...prev,
    name: payload.template_name_en,
    niceName: payload.template_name_he,
    category,
    disableSystemAutoUnsubscribe: false,
    components: [...headers, bodyComponent, ...footers, buttonsComponent],
  };
}

/** Legacy sidebar AI returns free text — apply only to BODY, preserve other components. */
export function applyLegacyAiTextToBody(prev: WhatsappEditTemplate, text: string): WhatsappEditTemplate {
  const body = text.trim();
  const varIndices = collectBodyVarIndices(body);
  const exampleRow = varIndices.map((_, i) => `ערך לדוגמה ${i + 1}`);
  const bodyComponent: WhatsappOriginalTemplateComponent = {
    type: 'BODY',
    format: null,
    text: body,
    buttons: null,
    example: varIndices.length > 0 ? { body_text: [exampleRow] } : undefined,
  };

  const rest = prev.components.filter((c) => c.type !== 'BODY');
  const headers = rest.filter((c) => c.type === 'HEADER');
  const footers = rest.filter((c) => c.type === 'FOOTER');
  const buttons = rest.filter((c) => c.type === 'BUTTONS');

  return {
    ...prev,
    components: [...headers, bodyComponent, ...footers, ...buttons],
  };
}
